import { valueError, valueLabel } from "../shared/value-copy";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  Dialog,
  Field,
  Inline,
  SelectField,
  Stack,
  StatusBadge
} from "@leylion/design-system";
import { getCustomerWallet } from "../api/client";
import type {
  Customer,
  OrderWorkflow,
  PaymentMethod
} from "../api/cashier-types";
import type { WalletAccountSummary } from "../api/crm-types";
import type { RuntimeSettings } from "../api/settings-types";
import { majorInputToMinor, minorToMajorInput } from "./cashier-money";
import "./cashier-v304-batch05.css";

type Language = "ar" | "en";
type PaymentMode = PaymentMethod | "Split";

export type PaymentCaptureRequest = {
  method: PaymentMethod;
  amountMajor: string;
  tenderedMajor: string;
  customerId: string | null;
  providerReference: string | null;
  clientOperationId: string;
};

export type SplitPaymentCaptureRequest = {
  allocations: PaymentCaptureRequest[];
};

export type SplitPaymentCaptureOutcome = {
  completed: boolean;
  capturedOperationIds: string[];
  failedOperationId: string | null;
  remainingMinor: number;
  errorMessage: string | null;
};

type SplitRow = {
  id: string;
  method: PaymentMethod;
  amountMajor: string;
  tenderedMajor: string;
  customerId: string;
  providerReference: string;
};

type Props = {
  open: boolean;
  language: Language;
  workflow: OrderWorkflow | null;
  runtimeSettings: RuntimeSettings | null;
  customers: Customer[];
  online: boolean;
  invoiceSection: ReactNode;
  formatMoney: (minor: number, currency?: string) => string;
  onClose: () => void;
  onCaptureSingle: (request: PaymentCaptureRequest) => Promise<void>;
  onCaptureSplit: (
    request: SplitPaymentCaptureRequest
  ) => Promise<SplitPaymentCaptureOutcome>;
  onError: (message: string) => void;
};

const MAX_SPLIT_PAYERS = 8;

function rowId(): string {
  return `payer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function methodLabel(method: PaymentMethod, ar: boolean): string {
  const labels: Record<PaymentMethod, { ar: string; en: string }> = {
    Cash: { ar: "نقدي", en: "Cash" },
    Card: { ar: "بطاقة", en: "Card" },
    GiftCard: { ar: "بطاقة هدية", en: "Gift card" },
    Wallet: { ar: "محفظة", en: "Wallet" },
    Account: { ar: "حساب آجل", en: "Account" }
  };
  return labels[method][ar ? "ar" : "en"];
}

function enabledMethods(settings: RuntimeSettings | null): PaymentMethod[] {
  const methods: PaymentMethod[] = [];
  if (settings?.cashPaymentEnabled !== false) methods.push("Cash");
  if (settings?.cardPaymentEnabled !== false) methods.push("Card");
  if (settings?.walletPaymentEnabled) methods.push("Wallet");
  methods.push("Account");
  return methods;
}

function distributeMinor(totalMinor: number, count: number): number[] {
  const safeCount = Math.max(1, count);
  const base = Math.floor(totalMinor / safeCount);
  const remainder = totalMinor - base * safeCount;
  return Array.from({ length: safeCount }, (_, index) =>
    base + (index < remainder ? 1 : 0)
  );
}

function customerLabel(customer: Customer): string {
  return [customer.name, customer.phone ?? customer.customerNumber]
    .filter(Boolean)
    .join(" · ");
}

function matchingCustomers(customers: Customer[], queryValue: string): Customer[] {
  const query = queryValue.trim().toLocaleLowerCase();
  if (!query) return customers;
  return customers.filter((customer) =>
    [customer.name, customer.phone, customer.customerNumber, customer.email]
      .some((value) => value?.toLocaleLowerCase().includes(query))
  );
}

export function CashierPaymentDialog({
  open,
  language,
  workflow,
  runtimeSettings,
  customers,
  online,
  invoiceSection,
  formatMoney,
  onClose,
  onCaptureSingle,
  onCaptureSplit
}: Props) {
  const ar = language === "ar";
  const methods = useMemo(() => enabledMethods(runtimeSettings), [runtimeSettings]);
  const currency = workflow?.order.currency ?? runtimeSettings?.currency ?? "AED";
  const balanceMinor = workflow?.order.balanceDueMinor ?? 0;
  const defaultMethod = methods[0] ?? "Account";

  const [mode, setMode] = useState<PaymentMode>(defaultMethod);
  const [singleMethod, setSingleMethod] = useState<PaymentMethod>(defaultMethod);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [tenderedAmount, setTenderedAmount] = useState("");
  const [providerReference, setProviderReference] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [wallet, setWallet] = useState<WalletAccountSummary | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);
  const [splitCustomerQueries, setSplitCustomerQueries] = useState<Record<string, string>>({});
  const [splitWallets, setSplitWallets] = useState<Record<string, WalletAccountSummary | null>>({});
  const [splitWalletErrors, setSplitWalletErrors] = useState<Record<string, string>>({});
  const [singleOperationId, setSingleOperationId] = useState(rowId());
  const [captureError, setCaptureError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !workflow) return;
    const nextMethod = enabledMethods(runtimeSettings)[0] ?? "Account";
    const amount = minorToMajorInput(workflow.order.balanceDueMinor, workflow.order.currency);
    const attachedCustomerId = workflow.customer?.id ?? "";
    const splitAmounts = distributeMinor(workflow.order.balanceDueMinor, 2);
    const secondary = enabledMethods(runtimeSettings).find((method) => method !== nextMethod) ?? nextMethod;

    setMode(nextMethod);
    setSingleMethod(nextMethod);
    setPaymentAmount(amount);
    setTenderedAmount(amount);
    setProviderReference("");
    setCustomerId(attachedCustomerId);
    setCustomerQuery("");
    setWallet(null);
    setWalletError("");
    setSingleOperationId(`single-${workflow.order.id}-${rowId()}`);
    setCaptureError("");
    setSplitCustomerQueries({});
    setSplitWallets({});
    setSplitWalletErrors({});
    setSplitRows([
      {
        id: rowId(),
        method: nextMethod,
        amountMajor: minorToMajorInput(splitAmounts[0] ?? 0, workflow.order.currency),
        tenderedMajor: minorToMajorInput(splitAmounts[0] ?? 0, workflow.order.currency),
        customerId: attachedCustomerId,
        providerReference: ""
      },
      {
        id: rowId(),
        method: secondary,
        amountMajor: minorToMajorInput(splitAmounts[1] ?? 0, workflow.order.currency),
        tenderedMajor: minorToMajorInput(splitAmounts[1] ?? 0, workflow.order.currency),
        customerId: attachedCustomerId,
        providerReference: ""
      }
    ]);
  }, [open, workflow?.order.id, runtimeSettings]);

  useEffect(() => {
    if (!open || mode !== "Wallet" || !customerId || !workflow) {
      setWallet(null);
      setWalletError("");
      setWalletLoading(false);
      return;
    }

    let cancelled = false;
    setWalletLoading(true);
    setWalletError("");
    getCustomerWallet(customerId, workflow.order.currency)
      .then((account) => {
        if (cancelled) return;
        setWallet(account);
        if (account.status !== "Active") {
          setWalletError(ar ? "محفظة العميل غير نشطة." : "Customer wallet is not active.");
        }
      })
      .catch((caught) => {
        if (cancelled) return;
        setWallet(null);
        setWalletError(valueError(caught, language));
      })
      .finally(() => {
        if (!cancelled) setWalletLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ar, customerId, mode, open, workflow?.order.currency]);

  const displayedCustomers = useMemo(
    () => matchingCustomers(customers, customerQuery),
    [customerQuery, customers]
  );

  const paymentAmountMinor = majorInputToMinor(paymentAmount, currency) ?? 0;
  const tenderedMinor = majorInputToMinor(tenderedAmount, currency) ?? 0;
  const singleRemainingMinor = Math.max(0, balanceMinor - paymentAmountMinor);
  const singleChangeMinor = singleMethod === "Cash"
    ? Math.max(0, tenderedMinor - paymentAmountMinor)
    : 0;

  const walletReady = mode !== "Wallet" || (
    Boolean(customerId) &&
    !walletLoading &&
    !walletError &&
    wallet?.status === "Active" &&
    (wallet?.balanceMinor ?? 0) >= paymentAmountMinor
  );
  const accountReady = mode !== "Account" || Boolean(customerId);
  const cashReady = mode !== "Cash" || tenderedMinor >= paymentAmountMinor;
  const singleReady =
    online &&
    paymentAmountMinor > 0 &&
    paymentAmountMinor <= balanceMinor &&
    walletReady &&
    accountReady &&
    cashReady;

  const splitAllocatedMinor = splitRows.reduce(
    (total, row) => total + (majorInputToMinor(row.amountMajor, currency) ?? 0),
    0
  );
  const splitRemainingMinor = Math.max(0, balanceMinor - splitAllocatedMinor);
  const splitChangeMinor = splitRows.reduce((total, row) => {
    if (row.method !== "Cash") return total;
    const amountMinor = majorInputToMinor(row.amountMajor, currency) ?? 0;
    const rowTenderedMinor = majorInputToMinor(row.tenderedMajor, currency) ?? 0;
    return total + Math.max(0, rowTenderedMinor - amountMinor);
  }, 0);

  const splitRowIsValid = (row: SplitRow): boolean => {
    const amountMinor = majorInputToMinor(row.amountMajor, currency) ?? 0;
    if (amountMinor <= 0) return false;
    if (row.method === "Cash") {
      return (majorInputToMinor(row.tenderedMajor, currency) ?? 0) >= amountMinor;
    }
    if (row.method === "Wallet") {
      const account = splitWallets[row.id];
      return Boolean(
        row.customerId &&
        account?.status === "Active" &&
        account.balanceMinor >= amountMinor &&
        !splitWalletErrors[row.id]
      );
    }
    if (row.method === "Account") return Boolean(row.customerId);
    return true;
  };

  const splitReady =
    online &&
    runtimeSettings?.splitPaymentsEnabled !== false &&
    splitRows.length >= 2 &&
    splitAllocatedMinor === balanceMinor &&
    splitRows.every(splitRowIsValid);

  const chooseMode = (next: PaymentMode) => {
    setCaptureError("");
    setMode(next);
    if (next !== "Split") {
      setSingleMethod(next);
      if (next !== "Cash") setTenderedAmount(paymentAmount);
    }
  };

  const updateSplitRow = (id: string, patch: Partial<SplitRow>) => {
    setSplitRows((current) => current.map((row) => row.id === id ? { ...row, ...patch } : row));
  };

  const loadSplitWallet = async (rowIdValue: string, selectedCustomerId: string) => {
    if (!selectedCustomerId || !workflow) {
      setSplitWallets((current) => ({ ...current, [rowIdValue]: null }));
      setSplitWalletErrors((current) => ({ ...current, [rowIdValue]: "" }));
      return;
    }
    setSplitWalletErrors((current) => ({ ...current, [rowIdValue]: "" }));
    try {
      const account = await getCustomerWallet(selectedCustomerId, workflow.order.currency);
      setSplitWallets((current) => ({ ...current, [rowIdValue]: account }));
      if (account.status !== "Active") {
        setSplitWalletErrors((current) => ({
          ...current,
          [rowIdValue]: ar ? "المحفظة غير نشطة." : "Wallet is not active."
        }));
      }
    } catch (caught) {
      setSplitWallets((current) => ({ ...current, [rowIdValue]: null }));
      setSplitWalletErrors((current) => ({
        ...current,
        [rowIdValue]: valueError(caught, language)
      }));
    }
  };

  const distributeSplitEqually = () => {
    const amounts = distributeMinor(balanceMinor, splitRows.length);
    setSplitRows((current) => current.map((row, index) => {
      const amount = minorToMajorInput(amounts[index] ?? 0, currency);
      return {
        ...row,
        amountMajor: amount,
        tenderedMajor: row.method === "Cash" ? amount : row.tenderedMajor
      };
    }));
  };

  const fillSplitRemaining = () => {
    setSplitRows((current) => {
      if (!current.length) return current;
      const allocatedBeforeLast = current.slice(0, -1).reduce(
        (total, row) => total + (majorInputToMinor(row.amountMajor, currency) ?? 0),
        0
      );
      const amount = minorToMajorInput(Math.max(0, balanceMinor - allocatedBeforeLast), currency);
      return current.map((row, index) => index === current.length - 1
        ? { ...row, amountMajor: amount, tenderedMajor: row.method === "Cash" ? amount : row.tenderedMajor }
        : row
      );
    });
  };

  const addSplitPayer = () => {
    if (splitRows.length >= MAX_SPLIT_PAYERS) return;
    const method = methods.find((item) => item === "Card") ?? methods[0] ?? "Account";
    setSplitRows((current) => [
      ...current,
      {
        id: rowId(),
        method,
        amountMajor: minorToMajorInput(0, currency),
        tenderedMajor: minorToMajorInput(0, currency),
        customerId: workflow?.customer?.id ?? "",
        providerReference: ""
      }
    ]);
  };

  const removeSplitPayer = () => {
    if (splitRows.length <= 2) return;
    const removed = splitRows[splitRows.length - 1];
    setSplitRows((current) => current.slice(0, -1));
    setSplitCustomerQueries((current) => {
      const next = { ...current };
      delete next[removed.id];
      return next;
    });
    setSplitWallets((current) => {
      const next = { ...current };
      delete next[removed.id];
      return next;
    });
    setSplitWalletErrors((current) => {
      const next = { ...current };
      delete next[removed.id];
      return next;
    });
  };

  const submitSingle = async () => {
    if (!singleReady) return;
    setCaptureError("");
    setSubmitting(true);
    try {
      await onCaptureSingle({
        method: singleMethod,
        amountMajor: paymentAmount,
        tenderedMajor: singleMethod === "Cash" ? tenderedAmount : paymentAmount,
        customerId: singleMethod === "Wallet" || singleMethod === "Account" ? customerId || null : null,
        providerReference: singleMethod === "Card" ? providerReference.trim() || null : null,
        clientOperationId: singleOperationId
      });
    } catch (caught) {
      setCaptureError(
        valueError(caught, language)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitSplit = async () => {
    if (!splitReady || !workflow) return;
    setCaptureError("");
    setSubmitting(true);
    const allocations = splitRows.map((row) => ({
      method: row.method,
      amountMajor: row.amountMajor,
      tenderedMajor: row.method === "Cash" ? row.tenderedMajor : row.amountMajor,
      customerId: row.method === "Wallet" || row.method === "Account" ? row.customerId || null : null,
      providerReference: row.method === "Card" ? row.providerReference.trim() || null : null,
      clientOperationId: `split-${workflow.order.id}-${row.id}`
    }));
    try {
      const outcome = await onCaptureSplit({ allocations });
      if (!outcome.completed) {
        setCaptureError(
          outcome.errorMessage ??
            (ar
              ? "تم حفظ الدفعات الناجحة. راجع الرصيد المتبقي وأكمل التحصيل."
              : "Successful allocations were retained. Review the remaining balance and continue collection.")
        );

        const failedIndex = allocations.findIndex(
          (allocation) => allocation.clientOperationId === outcome.failedOperationId
        );
        const failedRow = failedIndex >= 0 ? splitRows[failedIndex] : splitRows[0];
        const remaining = minorToMajorInput(outcome.remainingMinor, currency);
        if (failedRow) {
          setMode(failedRow.method);
          setSingleMethod(failedRow.method);
          setPaymentAmount(remaining);
          setTenderedAmount(remaining);
          setCustomerId(failedRow.customerId);
          setProviderReference(failedRow.providerReference);
          setSingleOperationId(
            `recovery-${workflow.order.id}-${failedRow.id}-${outcome.remainingMinor}`
          );
        }
      }
    } catch (caught) {
      setCaptureError(
        valueError(caught, language)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const methodTabs: Array<{ mode: PaymentMode; labelAr: string; labelEn: string; caption: string }> = [
    ...(methods.includes("Cash") ? [{ mode: "Cash" as const, labelAr: "نقدي", labelEn: "Cash", caption: "Cash" }] : []),
    ...(methods.includes("Card") ? [{ mode: "Card" as const, labelAr: "بطاقة", labelEn: "Card", caption: "Card" }] : []),
    ...(methods.includes("Wallet") ? [{ mode: "Wallet" as const, labelAr: "محفظة", labelEn: "Wallet", caption: "Wallet" }] : []),
    { mode: "Account" as const, labelAr: "حساب", labelEn: "Account", caption: "Account" },
    ...(runtimeSettings?.splitPaymentsEnabled !== false
      ? [{ mode: "Split" as const, labelAr: "تقسيم", labelEn: "Split", caption: "Split" }]
      : [])
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="cashier-payment-dialog"
      title={ar ? "تحصيل الدفع" : "Collect payment"}
      description={ar
        ? "اختر طريقة الدفع وسجّل المبلغ المستلم."
        : "Choose the payment method and record the received amount."}
      footer={
        <Inline>
          <Button variant="secondary" disabled={submitting} onClick={onClose}>
            {ar ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            data-testid="capture-payment"
            disabled={submitting || (mode === "Split" ? !splitReady : !singleReady)}
            onClick={() => void (mode === "Split" ? submitSplit() : submitSingle())}
          >
            {submitting
              ? ar ? "جارٍ التحصيل…" : "Capturing…"
              : ar ? "تأكيد الدفع" : "Confirm payment"}
          </Button>
        </Inline>
      }
    >
      {workflow ? (
        <div className="cashier-payment-dialog__body">
          {captureError ? (
            <Alert
              tone="danger"
              title={ar ? "تعذر إكمال التحصيل" : "Collection needs attention"}
              data-testid="payment-capture-recovery"
            >
              {captureError}
            </Alert>
          ) : null}

          <section className="cashier-payment-dialog__amount-card">
            <div>
              <small>{ar ? "المبلغ المستحق" : "Amount due"}</small>
              <strong>{formatMoney(balanceMinor, currency)}</strong>
            </div>
            <div>
              <small>{ar ? "الطلب" : "Order"}</small>
              <strong>{/^\d+$/.test(workflow.order.orderNumber) ? `#${workflow.order.orderNumber}` : workflow.order.orderNumber}</strong>
            </div>
          </section>

          <div className="cashier-payment-dialog__methods" role="group" aria-label={ar ? "طرق الدفع" : "Payment methods"}>
            <select
              data-testid="payment-method"
              className="cashier-payment-dialog__compat-select"
              tabIndex={-1}
              value={singleMethod}
              onChange={(event) => chooseMode(event.target.value as PaymentMethod)}
            >
              {methods.map((method) => <option key={method} value={method}>{method}</option>)}
            </select>
            {methodTabs.map((item) => (
              <button
                key={item.mode}
                type="button"
                className={mode === item.mode ? "is-active" : ""}
                data-testid={`payment-method-${item.mode.toLowerCase()}`}
                aria-pressed={mode === item.mode}
                onClick={() => chooseMode(item.mode)}
              >
                <strong>{ar ? item.labelAr : item.labelEn}</strong>
                <small>{item.caption}</small>
              </button>
            ))}
          </div>

          {mode !== "Split" ? (
            <Stack>
              {(mode === "Wallet" || mode === "Account") ? (
                <Card className="cashier-payment-dialog__customer" title={mode === "Wallet"
                  ? ar ? "محفظة العميل" : "Customer wallet"
                  : ar ? "حساب العميل" : "Customer account"}
                  description={mode === "Wallet"
                    ? ar ? "ابحث عن العميل وتحقق من الرصيد قبل الخصم." : "Find the customer and verify the balance before debit."
                    : ar ? "يجب ربط العميل قبل تسجيل البيع الآجل." : "Attach a customer before posting the credit sale."}
                >
                  <Stack>
                    <Field
                      data-testid="payment-customer-search"
                      label={ar ? "بحث العميل" : "Customer search"}
                      placeholder={ar ? "الاسم أو الهاتف أو رقم العميل" : "Name, phone or customer number"}
                      value={customerQuery}
                      onChange={(event) => setCustomerQuery(event.target.value)}
                    />
                    {customerQuery.trim() ? (
                      <div className="cashier-payment-dialog__customer-results" data-testid="payment-customer-results">
                        {displayedCustomers.length ? displayedCustomers.slice(0, 8).map((customer) => (
                          <button
                            type="button"
                            key={customer.id}
                            className={customerId === customer.id ? "is-selected" : ""}
                            onClick={() => {
                              setCustomerId(customer.id);
                              setCustomerQuery(customerLabel(customer));
                            }}
                          >
                            <strong>{customer.name}</strong>
                            <small>{customer.phone ?? customer.customerNumber}</small>
                          </button>
                        )) : (
                          <span className="cashier-payment-dialog__customer-empty">{ar ? "لا يوجد عميل مطابق." : "No matching customer."}</span>
                        )}
                      </div>
                    ) : null}
                    <SelectField
                      data-testid="payment-customer"
                      label={ar ? "العميل" : "Customer"}
                      value={customerId}
                      onChange={(event) => setCustomerId(event.target.value)}
                    >
                      <option value="">{ar ? "اختر عميلاً" : "Select customer"}</option>
                      {displayedCustomers.map((customer) => (
                        <option key={customer.id} value={customer.id}>{customerLabel(customer)}</option>
                      ))}
                    </SelectField>
                    {mode === "Wallet" ? (
                      walletLoading ? (
                        <StatusBadge tone="info">{ar ? "جارٍ التحقق من المحفظة…" : "Checking wallet…"}</StatusBadge>
                      ) : walletError ? (
                        <Alert tone="danger" title={ar ? "تعذر استخدام المحفظة" : "Wallet unavailable"}>{walletError}</Alert>
                      ) : wallet ? (
                        <div className="cashier-payment-dialog__wallet-card" data-testid="payment-wallet-summary">
                          <span>
                            <small>{ar ? "الرصيد المتاح" : "Available balance"}</small>
                            <strong>{formatMoney(wallet.balanceMinor, wallet.currency)}</strong>
                          </span>
                          <StatusBadge tone={wallet.balanceMinor >= paymentAmountMinor ? "success" : "warning"}>
                            {valueLabel(wallet.status, language)}
                          </StatusBadge>
                        </div>
                      ) : (
                        <Alert tone="info" title={ar ? "بيانات العميل مطلوبة" : "Customer required"}>
                          {ar ? "حدد العميل للتحقق من رصيد المحفظة." : "Select the customer to verify the wallet balance."}
                        </Alert>
                      )
                    ) : (
                      <Alert tone="warning" title={ar ? "بيع آجل — ليس مقبوضاً" : "Credit sale — not collected"}>
                        {ar
                          ? "سيُرحّل المبلغ إلى الذمم المدينة في ملف العميل. لن يدخل المبلغ ضمن مقبوضات الوردية."
                          : "The amount posts to accounts receivable. It will not enter shift collections."}
                      </Alert>
                    )}
                  </Stack>
                </Card>
              ) : null}

              <section className="cashier-payment-dialog__direct">
                <Field
                  data-testid="payment-amount"
                  label={ar ? "هذه الدفعة" : "This payment"}
                  inputMode="decimal"
                  value={paymentAmount}
                  onChange={(event) => {
                    setPaymentAmount(event.target.value);
                    if (singleMethod !== "Cash") setTenderedAmount(event.target.value);
                  }}
                />
                <Field
                  data-testid="payment-tendered"
                  label={ar ? "المبلغ المستلم" : "Tendered amount"}
                  inputMode="decimal"
                  disabled={singleMethod !== "Cash"}
                  value={tenderedAmount}
                  onChange={(event) => setTenderedAmount(event.target.value)}
                />
                {singleMethod === "Cash" ? (
                  <div className="cashier-payment-dialog__cash-tools">
                    <Button size="sm" variant="secondary" onClick={() => setTenderedAmount(paymentAmount)}>
                      {ar ? "المبلغ كامل" : "Exact amount"}
                    </Button>
                    {[50, 100, 200, 500].map((value) => (
                      <button key={value} type="button" onClick={() => setTenderedAmount(value.toFixed(2))}>{value}</button>
                    ))}
                  </div>
                ) : null}
              </section>

              {singleMethod === "Card" ? (
                <Stack>
                  <Field
                    label={ar ? "مرجع جهاز الدفع" : "Terminal reference"}
                    value={providerReference}
                    onChange={(event) => setProviderReference(event.target.value)}
                  />
                  <Alert tone="info" title={ar ? "وضع UAT" : "UAT mode"}>
                    {ar
                      ? "يمكن ترك المرجع فارغاً في UAT؛ يستخدم الخادم الجهاز التجريبي خارج الإنتاج فقط."
                      : "The reference may be blank in UAT; the server uses the mock terminal outside Production only."}
                  </Alert>
                </Stack>
              ) : null}

              <section className="cashier-payment-dialog__summary" aria-label={ar ? "ملخص الدفع" : "Payment summary"}>
                <div><span>{ar ? "المتبقي قبل الدفع" : "Balance before"}</span><strong>{formatMoney(balanceMinor, currency)}</strong></div>
                <div><span>{ar ? "هذه الدفعة" : "This payment"}</span><strong>{formatMoney(paymentAmountMinor, currency)}</strong></div>
                <div className="is-grand"><span>{ar ? "المتبقي بعد الدفع" : "Balance after"}</span><strong>{formatMoney(singleRemainingMinor, currency)}</strong></div>
                {singleMethod === "Cash" && runtimeSettings?.cashChangeEnabled !== false ? (
                  <div className="is-change"><span>{ar ? "الباقي للعميل" : "Customer change"}</span><strong>{formatMoney(singleChangeMinor, currency)}</strong></div>
                ) : null}
              </section>
            </Stack>
          ) : (
            <Stack>
              <section className="cashier-payment-dialog__split-head">
                <div>
                  <strong>{ar ? "تقسيم الدفعة" : "Split payment"}</strong>
                  <small>{ar ? "حدد عدد الدافعين وطريقة كل دفعة." : "Set the payer count and tender for each allocation."}</small>
                </div>
                <div className="cashier-payment-dialog__payer-count">
                  <button type="button" disabled={splitRows.length <= 2} onClick={removeSplitPayer}>−</button>
                  <strong>{splitRows.length}</strong>
                  <button type="button" disabled={splitRows.length >= MAX_SPLIT_PAYERS} onClick={addSplitPayer}>+</button>
                </div>
              </section>

              <div className="cashier-payment-dialog__split-list">
                {splitRows.map((row, index) => {
                  const amountMinor = majorInputToMinor(row.amountMajor, currency) ?? 0;
                  const rowWallet = splitWallets[row.id];
                  return (
                    <article className="cashier-payment-dialog__payer" key={row.id}>
                      <span className="cashier-payment-dialog__payer-index">{index + 1}</span>
                      <SelectField
                        label={ar ? "الطريقة" : "Method"}
                        value={row.method}
                        onChange={(event) => {
                          const method = event.target.value as PaymentMethod;
                          updateSplitRow(row.id, {
                            method,
                            tenderedMajor: method === "Cash" ? row.amountMajor : row.tenderedMajor
                          });
                          if (method === "Wallet" && row.customerId) {
                            void loadSplitWallet(row.id, row.customerId);
                          }
                        }}
                      >
                        {methods.map((method) => <option key={method} value={method}>{methodLabel(method, ar)}</option>)}
                      </SelectField>
                      <Field
                        label={ar ? "المبلغ" : "Amount"}
                        inputMode="decimal"
                        value={row.amountMajor}
                        onChange={(event) => updateSplitRow(row.id, {
                          amountMajor: event.target.value,
                          tenderedMajor: row.method === "Cash" ? event.target.value : row.tenderedMajor
                        })}
                      />
                      {row.method === "Cash" ? (
                        <Field
                          label={ar ? "المستلم" : "Tendered"}
                          inputMode="decimal"
                          value={row.tenderedMajor}
                          onChange={(event) => updateSplitRow(row.id, { tenderedMajor: event.target.value })}
                        />
                      ) : null}
                      {row.method === "Card" ? (
                        <Field
                          label={ar ? "مرجع البطاقة" : "Card reference"}
                          value={row.providerReference}
                          onChange={(event) => updateSplitRow(row.id, { providerReference: event.target.value })}
                        />
                      ) : null}
                      {row.method === "Wallet" ? (
                        <Stack>
                          <Field
                            data-testid={`split-wallet-customer-search-${index}`}
                            label={ar ? "بحث العميل" : "Customer search"}
                            placeholder={ar ? "الاسم أو الهاتف أو رقم العميل" : "Name, phone or customer number"}
                            value={splitCustomerQueries[row.id] ?? ""}
                            onChange={(event) => setSplitCustomerQueries((current) => ({ ...current, [row.id]: event.target.value }))}
                          />
                          {(splitCustomerQueries[row.id] ?? "").trim() ? (
                            <div className="cashier-payment-dialog__customer-results" data-testid={`split-wallet-customer-results-${index}`}>
                              {matchingCustomers(customers, splitCustomerQueries[row.id] ?? "").slice(0, 8).length
                                ? matchingCustomers(customers, splitCustomerQueries[row.id] ?? "").slice(0, 8).map((customer) => (
                                    <button
                                      type="button"
                                      key={customer.id}
                                      className={row.customerId === customer.id ? "is-selected" : ""}
                                      onClick={() => {
                                        updateSplitRow(row.id, { customerId: customer.id });
                                        setSplitCustomerQueries((current) => ({ ...current, [row.id]: customerLabel(customer) }));
                                        void loadSplitWallet(row.id, customer.id);
                                      }}
                                    >
                                      <strong>{customer.name}</strong>
                                      <small>{customer.phone ?? customer.customerNumber}</small>
                                    </button>
                                  ))
                                : <span className="cashier-payment-dialog__customer-empty">{ar ? "لا يوجد عميل مطابق." : "No matching customer."}</span>}
                            </div>
                          ) : null}
                        </Stack>
                      ) : null}
                      {row.method === "Wallet" || row.method === "Account" ? (
                        <SelectField
                          label={ar ? "العميل" : "Customer"}
                          value={row.customerId}
                          onChange={(event) => {
                            const nextCustomerId = event.target.value;
                            updateSplitRow(row.id, { customerId: nextCustomerId });
                            if (row.method === "Wallet") {
                              const selectedCustomer = customers.find((customer) => customer.id === nextCustomerId);
                              setSplitCustomerQueries((current) => ({ ...current, [row.id]: selectedCustomer ? customerLabel(selectedCustomer) : "" }));
                              void loadSplitWallet(row.id, nextCustomerId);
                            }
                          }}
                        >
                          <option value="">{ar ? "اختر عميلاً" : "Select customer"}</option>
                          {(row.method === "Wallet"
                            ? matchingCustomers(customers, splitCustomerQueries[row.id] ?? "")
                            : customers
                          ).map((customer) => <option key={customer.id} value={customer.id}>{customerLabel(customer)}</option>)}
                        </SelectField>
                      ) : null}
                      {row.method === "Wallet" ? (
                        splitWalletErrors[row.id] ? (
                          <small className="is-error">{splitWalletErrors[row.id]}</small>
                        ) : rowWallet ? (
                          <small>{ar ? "رصيد المحفظة" : "Wallet balance"}: <strong>{formatMoney(rowWallet.balanceMinor, rowWallet.currency)}</strong></small>
                        ) : (
                          <small>{ar ? "حدد العميل للتحقق من المحفظة" : "Select customer to verify wallet"}</small>
                        )
                      ) : null}
                      {row.method === "Cash" ? (
                        <small>{ar ? "الباقي" : "Change"}: <strong>{formatMoney(Math.max(0, (majorInputToMinor(row.tenderedMajor, currency) ?? 0) - amountMinor), currency)}</strong></small>
                      ) : null}
                    </article>
                  );
                })}
              </div>

              <div className="cashier-payment-dialog__split-tools">
                <Button size="sm" variant="secondary" onClick={distributeSplitEqually}>{ar ? "توزيع بالتساوي" : "Distribute equally"}</Button>
                <Button size="sm" variant="secondary" onClick={fillSplitRemaining}>{ar ? "إكمال المتبقي" : "Fill remaining"}</Button>
                <Button size="sm" variant="secondary" onClick={() => setSplitRows((current) => current.map((row) => ({ ...row, amountMajor: minorToMajorInput(0, currency), tenderedMajor: minorToMajorInput(0, currency) })))}>{ar ? "مسح" : "Clear"}</Button>
              </div>

              <section className="cashier-payment-dialog__split-summary">
                <div><span>{ar ? "الموزع" : "Allocated"}</span><strong>{formatMoney(splitAllocatedMinor, currency)}</strong></div>
                <div className={splitRemainingMinor === 0 ? "is-balanced" : "is-warning"}><span>{ar ? "غير موزع" : "Unallocated"}</span><strong>{formatMoney(splitRemainingMinor, currency)}</strong></div>
                <div><span>{ar ? "الباقي النقدي" : "Cash change"}</span><strong>{formatMoney(splitChangeMinor, currency)}</strong></div>
                <div><span>{ar ? "الدافعون" : "Payers"}</span><strong>{splitRows.length}</strong></div>
              </section>
            </Stack>
          )}

          {invoiceSection}

          <small className="cashier-payment-dialog__note">
            {ar
              ? "الدفع عملية مالية مستقلة عن إرسال الطلب للتحضير. التحويل البنكي غير ظاهر لأنه لا يوجد له عقد تحصيل إنتاجي حالياً."
              : "Payment is financially independent from preparation routing. Bank transfer is not shown because no Production capture contract currently exists."}
          </small>
        </div>
      ) : null}
    </Dialog>
  );
}
